import { PaymentMethodType, ProcessingLenderStrategyType } from '@nemuru-eng/js-api-client';

import { LoanApplicationEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/LoanApplication/LoanApplicationEntity';
import { AbstractProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/AbstractProcessingStrategyEntity';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';

export class ParallelProcessingStrategyEntity extends AbstractProcessingStrategyEntity {
  constructor(paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity) {
    super(paymentMethodDetailEntity);
  }

  public static isType(strategyType: ProcessingLenderStrategyType): boolean {
    return strategyType === ProcessingLenderStrategyType.PARALLEL;
  }

  protected sortLoanApplications(loanApplications: LoanApplicationEntity[]): LoanApplicationEntity[] {
    // TODO: This should be determined by the processing strategy
    // We are assuming that lowest apr and lowest total amount are the only conditions that matter
    if (!this._paymentMethodConditions) {
      return loanApplications;
    }

    const paymentMethodType = this._paymentMethodDetailEntity.type;

    if (paymentMethodType === PaymentMethodType.CARD_ONBOARDING || paymentMethodType === PaymentMethodType.CREDIT_LINE) {
      return loanApplications;
    }

    const paymentMethodConditions = this._paymentMethodConditions.find((pmc) => pmc.type === paymentMethodType);

    if (!paymentMethodConditions) {
      return loanApplications;
    }

    // Sort by lowest apr and, if same apr, by lowest total amount
    // The apr and total amount are in the conditions
    // You can join conditions and loan applications by lenderName
    const sortedLoanApplications = loanApplications.sort((a, b) => {
      const conditionA = this._paymentMethodConditions?.find(
        (pmc) => pmc.conditions?.lenderName === a.lenderName,
      )?.conditions;
      const conditionB = this._paymentMethodConditions?.find(
        (pmc) => pmc.conditions?.lenderName === b.lenderName,
      )?.conditions;

      if (!conditionA || !conditionB) {
        return 0;
      }

      if (conditionA.apr.value < conditionB.apr.value) return -1;
      if (conditionA.apr.value > conditionB.apr.value) return 1;
      if (conditionA.totalAmount.value < conditionB.totalAmount.value) return -1;
      if (conditionA.totalAmount.value > conditionB.totalAmount.value) return 1;
      return 0;
    });
    return sortedLoanApplications;
  }

  protected setProcessingStatus(loanApplications: LoanApplicationEntity[]): LoanApplicationEntity[] {
    return loanApplications.map((loanApplication) => {
      if (loanApplication.isNotProcessed()) {
        loanApplication.isProcessing = true;
        loanApplication.alreadyProcessed = false;
      } else {
        loanApplication.isProcessing = false;
        loanApplication.alreadyProcessed = true;
      }
      return loanApplication;
    });
  }

  public allowProcessing(loanApplicationId: string): boolean {
    if (!this._loanApplications) return false;

    const loanApplication = this._loanApplications.find((la) => la.id === loanApplicationId);
    if (!loanApplication) return false;

    if (loanApplication.isNotProcessed()) return true;
    if (loanApplication.hasErrors()) return true;

    return false;
  }

  public clone(): this {
    const clone = new ParallelProcessingStrategyEntity(this._paymentMethodDetailEntity);

    // Copy all properties from this to clone
    Object.assign(clone, this);

    return clone as this;
  }
}
